/**
 * components/import/statements/StatementProcessor.jsx — Phase 84.3 (groundwork)
 *
 * Canonical mount point for the statement-import experience. This is the
 * lightweight surface the adaptive Import Hub (`pages/ImportHub.jsx`) uses
 * when a user has already dropped a file that was classified as a bank /
 * card statement. Callers pass:
 *
 *   • prefillFile  — the File the user already dropped in the hub.
 *                    The underlying importer auto-starts on it once
 *                    accounts are loaded (see _StatementImportInner in
 *                    pages/CSVImport.js — the useEffect labeled 84.2.1).
 *   • onBack       — called when the user wants to return to the picker.
 *
 * We intentionally keep this component thin. It exists to give the hub
 * (and any future caller) a stable import path so the eventual full
 * decomposition of `pages/CSVImport.js` (still 3.9k LOC) can proceed
 * behind a shim without touching call-sites.
 *
 * IMPORTANT: The legacy standalone route `/import/statements` continues
 * to render the same underlying default export directly — that path is
 * NOT routed through this wrapper, so nothing on that flow can regress.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import StatementImport from '@/pages/CSVImport';

export default function StatementProcessor({ prefillFile = null, onBack = null }) {
  return (
    <div className="space-y-4" data-testid="statement-processor">
      {onBack && (
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="rounded-lg -ml-2 text-muted-foreground hover:text-foreground"
            data-testid="statement-processor-back"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
            Back to Import picker
          </Button>
        </div>
      )}
      <StatementImport prefillFile={prefillFile} embedded={true} />
    </div>
  );
}
